import React, { useState, useMemo, useEffect } from 'react';
import {
  FolderOpen,
  Search,
  Loader2,
  AlertCircle,
  RefreshCw,
  ArrowDownLeft,
  ArrowUpRight,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePartnerAuth } from '@/contexts/PartnerAuthContext';
import { usePartnerProjects } from '@/hooks/usePartnerProjects';
import { PartnerProjectCard } from '@/components/partner/PartnerProjectCard';
import { getPartnerOutboundReferrals, updateOutboundReferralStatus } from '@/services/outboundReferrals';
import type { PartnerOutboundReferralView, OutboundReferralStatus } from '@/types';

type TabType = 'inbound' | 'outbound';

export function PartnerDashboardPage() {
  const { companyName, businessVisibility, sessionToken } = usePartnerAuth();
  const { data: projects, isLoading, isError, error, refetch } = usePartnerProjects();

  const [activeTab, setActiveTab] = useState<TabType>('inbound');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Outbound referrals state
  const [outboundReferrals, setOutboundReferrals] = useState<PartnerOutboundReferralView[]>([]);
  const [loadingOutbound, setLoadingOutbound] = useState(false);
  const [outboundError, setOutboundError] = useState<string | null>(null);

  // Fetch outbound referrals
  useEffect(() => {
    async function fetchOutbound() {
      if (!sessionToken) return;
      setLoadingOutbound(true);
      setOutboundError(null);
      try {
        const result = await getPartnerOutboundReferrals(sessionToken);
        if (result.success && result.referrals) {
          setOutboundReferrals(result.referrals);
        } else {
          setOutboundError(result.error || 'Failed to load referrals');
        }
      } catch (err) {
        console.error('Failed to fetch outbound referrals:', err);
        setOutboundError('Failed to load referrals');
      } finally {
        setLoadingOutbound(false);
      }
    }
    fetchOutbound();
  }, [sessionToken]);

  // Handle status update
  const handleStatusUpdate = async (referralId: string, newStatus: OutboundReferralStatus, notes?: string) => {
    if (!sessionToken) return;
    try {
      const result = await updateOutboundReferralStatus(sessionToken, referralId, newStatus, notes);
      if (result.success) {
        toast.success('Status updated');
        setOutboundReferrals(outboundReferrals.map(r =>
          r.id === referralId ? { ...r, status: newStatus, statusNotes: notes } : r
        ));
      } else {
        toast.error(result.error || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    if (!projects) return [];
    const statuses = new Set(projects.map((p) => p.status));
    return Array.from(statuses).sort();
  }, [projects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    return projects.filter((project) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!project.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && project.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [projects, searchQuery, statusFilter]);

  // Get business visibility label
  const getVisibilityLabel = () => {
    switch (businessVisibility) {
      case 'both':
        return 'All Businesses';
      case 'azdisaster':
        return 'ACR';
      case 'blacksmith':
        return 'Blacksmith';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Partner Portal</h1>
        <p className="text-gray-600 mt-1">
          Welcome, {companyName}
          {businessVisibility !== 'both' && (
            <span className="text-gray-500"> ({getVisibilityLabel()})</span>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('inbound')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'inbound'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <ArrowDownLeft size={18} />
            Projects You Referred
            {projects && projects.length > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                {projects.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('outbound')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'outbound'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <ArrowUpRight size={18} />
            Referrals Sent to You
            {outboundReferrals.length > 0 && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">
                {outboundReferrals.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Inbound Tab Content */}
      {activeTab === 'inbound' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="w-full sm:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {uniqueStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh */}
              <button
                onClick={() => refetch()}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <p className="text-gray-600 mt-4">Loading projects...</p>
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
              <p className="text-red-800 font-medium">Failed to load projects</p>
              <p className="text-red-600 text-sm mt-1">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && filteredProjects.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <FolderOpen className="mx-auto text-gray-300 mb-4" size={64} />
              {projects && projects.length > 0 ? (
                <>
                  <p className="text-gray-900 font-medium text-lg">
                    No matching projects
                  </p>
                  <p className="text-gray-500 mt-2">
                    Try adjusting your search or filter criteria.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-900 font-medium text-lg">
                    No projects yet
                  </p>
                  <p className="text-gray-500 mt-2">
                    Projects you refer will appear here once they're in our system.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Projects Grid */}
          {!isLoading && !isError && filteredProjects.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => (
                <PartnerProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}

          {/* Project Count */}
          {!isLoading && !isError && projects && projects.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {filteredProjects.length} of {projects.length} projects
            </p>
          )}
        </>
      )}

      {/* Outbound Tab Content */}
      {activeTab === 'outbound' && (
        <>
          {/* Loading State */}
          {loadingOutbound && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
              <p className="text-gray-600 mt-4">Loading referrals...</p>
            </div>
          )}

          {/* Error State */}
          {outboundError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
              <p className="text-red-800 font-medium">Failed to load referrals</p>
              <p className="text-red-600 text-sm mt-1">{outboundError}</p>
            </div>
          )}

          {/* Empty State */}
          {!loadingOutbound && !outboundError && outboundReferrals.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <ArrowUpRight className="mx-auto text-gray-300 mb-4" size={64} />
              <p className="text-gray-900 font-medium text-lg">No referrals yet</p>
              <p className="text-gray-500 mt-2">
                When we send you job leads, they'll appear here.
              </p>
            </div>
          )}

          {/* Referrals List */}
          {!loadingOutbound && !outboundError && outboundReferrals.length > 0 && (
            <div className="space-y-4">
              {outboundReferrals.map((referral) => (
                <OutboundReferralCard
                  key={referral.id}
                  referral={referral}
                  onStatusUpdate={handleStatusUpdate}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ===========================================
// Outbound Referral Card Component
// ===========================================

interface OutboundReferralCardProps {
  referral: PartnerOutboundReferralView;
  onStatusUpdate: (referralId: string, status: OutboundReferralStatus, notes?: string) => void;
}

function OutboundReferralCard({ referral, onStatusUpdate }: OutboundReferralCardProps) {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<OutboundReferralStatus>(referral.status);
  const [notes, setNotes] = useState(referral.statusNotes || '');

  const statusConfig: Record<OutboundReferralStatus, { label: string; color: string; icon: React.ReactNode }> = {
    sent: {
      label: 'Sent',
      color: 'bg-gray-100 text-gray-700',
      icon: <Clock size={14} />,
    },
    contacted: {
      label: 'Contacted',
      color: 'bg-blue-100 text-blue-700',
      icon: <Phone size={14} />,
    },
    in_progress: {
      label: 'In Progress',
      color: 'bg-yellow-100 text-yellow-700',
      icon: <Clock size={14} />,
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-700',
      icon: <CheckCircle size={14} />,
    },
    declined: {
      label: 'Declined',
      color: 'bg-red-100 text-red-700',
      icon: <XCircle size={14} />,
    },
  };

  const { label, color, icon } = statusConfig[referral.status] || statusConfig.sent;

  const handleSaveStatus = () => {
    onStatusUpdate(referral.id, selectedStatus, notes || undefined);
    setShowStatusModal(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-gray-900">{referral.clientName}</h3>
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${color}`}>
              {icon}
              {label}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              referral.business === 'azdisaster'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {referral.business === 'azdisaster' ? 'ACR' : 'Blacksmith'}
            </span>
          </div>

          <p className="text-sm text-gray-600 mt-2">{referral.description}</p>

          <div className="flex gap-4 mt-3 text-sm">
            {referral.clientPhone && (
              <a href={`tel:${referral.clientPhone}`} className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                <Phone size={14} />
                {referral.clientPhone}
              </a>
            )}
            {referral.clientEmail && (
              <a href={`mailto:${referral.clientEmail}`} className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                <Mail size={14} />
                {referral.clientEmail}
              </a>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Sent by {referral.sentByName} on {new Date(referral.sentAt).toLocaleDateString()}
          </p>

          {referral.statusNotes && (
            <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
              <strong>Your notes:</strong> {referral.statusNotes}
            </div>
          )}
        </div>

        <button
          onClick={() => setShowStatusModal(true)}
          className="px-3 py-1.5 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        >
          Update Status
        </button>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Update Referral Status</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update the status for {referral.clientName}
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['contacted', 'in_progress', 'completed', 'declined'] as OutboundReferralStatus[]).map((status) => {
                    const cfg = statusConfig[status];
                    return (
                      <button
                        key={status}
                        onClick={() => setSelectedStatus(status)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2 ${
                          selectedStatus === status
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {cfg.icon}
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about the status..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStatus}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Save Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
